import type { NavIdProps } from '@vkontakte/vkui';
import { cabinetApi } from '../../api/cabinet';
import { useLoader } from '../../api/loader';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import {
  CardList,
  EmptyState,
  ErrorState,
  Gutter,
  LoadingState,
  Muted,
  Notice,
  Screen,
  Section,
  SectionTitle,
} from '../../shared/ui';
import { FieldDiffList } from './FieldDiff';

/**
 * «Что изменилось» — ОТДЕЛЬНЫЙ экран, как в макете.
 *
 * Раскрывашкой внутри карточки анкеты сравнение читалось плохо: у каждого поля
 * по две полосы текста, и вместе с карточкой они складывались в ленту, из
 * которой не видно, где кончается одно и начинается другое.
 */
export function FormDiffPanel({ id }: NavIdProps) {
  const back = useBack();

  return (
    <Screen id={id} title={t('character.diff.title')} back={back}>
      <FormDiff />
    </Screen>
  );
}

/**
 * Сравнение принятой версии с той, что игрок правит.
 *
 * Считает его сервер (GET /api/characters/me/form/diff) — тем же кодом, каким
 * пользуется экран модерации. Считать разницу ещё и на фронте значит однажды
 * показать игроку одно, а модератору другое.
 */
function FormDiff() {
  const diff = useLoader(() => cabinetApi.formDiff(), []);

  if (diff.loading && diff.data === null) return <LoadingState />;

  if (diff.error !== null && diff.data === null) {
    return <ErrorState message={diff.error} onRetry={diff.reload} />;
  }

  const data = diff.data;
  if (data === null) return null;

  if (data.openVersionId === null) {
    return (
      <EmptyState title={t('character.diff.empty.noOpen.title')}>
        {t('character.diff.empty.noOpen.text')}
      </EmptyState>
    );
  }

  if (data.fields.length === 0) {
    return (
      <EmptyState
        title={
          data.publishedVersionId === null
            ? t('character.diff.empty.noBase.title')
            : t('character.diff.empty.same.title')
        }
      >
        {data.publishedVersionId === null
          ? t('character.diff.empty.noBase.text')
          : t('character.diff.empty.same.text')}
      </EmptyState>
    );
  }

  return (
    <Section>
      <Gutter>
        <Muted>
          {data.publishedVersionId === null
            ? t('character.diff.hint.first')
            : t('character.diff.hint.compare')}
        </Muted>
      </Gutter>

      <SectionTitle count={data.fields.length}>{t('character.diff.section.changes')}</SectionTitle>

      <CardList>
        <FieldDiffList
          changes={data.fields}
          beforeLabel={
            data.publishedVersionId === null
              ? t('character.diff.label.before')
              : t('character.diff.label.published')
          }
          afterLabel={
            data.publishedVersionId === null
              ? t('character.diff.label.after')
              : t('character.diff.label.newVersion')
          }
        />
      </CardList>

      {/* Внутри раздела, а не после него: шов над полосой даёт раздел, а за
          его пределами она прилипала к последней карточке. */}
      <Gutter>
        <Notice tone="pending">{t('character.diff.notice.untilApproved')}</Notice>
      </Gutter>
    </Section>
  );
}
